import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { settings as settingsApi, projects as projectsApi } from '@/utils/api';
import {
  LayoutDashboard, TrendingUp, Bug, FileText, Users, Clock,
  ArrowRight, AlertCircle, CheckCircle2, CircleDot, Sparkles
} from 'lucide-react';

const STATUS_COLORS = {
  'New': { bg: '#E6F1F4', color: '#006080' },
  'Scoping': { bg: '#E6F1F4', color: '#006080' },
  'Estimate Sent': { bg: '#FFF3CD', color: '#D9A441' },
  'Approved': { bg: '#D4EDDA', color: '#3C8A5A' },
  'In Progress': { bg: '#E6F1F4', color: '#006080' },
  'UAT': { bg: '#E8F4FD', color: '#0066CC' },
  'Delivered': { bg: '#D4EDDA', color: '#3C8A5A' },
  'Archived': { bg: '#F5F5F5', color: '#5C6B72' },
};

function StatCard({ icon: Icon, label, value, color, subtext }) {
  return (
    <div className="card-base p-5 flex items-start gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-10 h-10 rounded-btn flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={20} style={{ color }} strokeWidth={2} />
      </div>
      <div>
        <p className="text-2xl font-black" style={{ color: 'var(--ink)' }}>{value}</p>
        <p className="text-sm font-medium" style={{ color: 'var(--muted)' }}>{label}</p>
        {subtext && <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{subtext}</p>}
      </div>
    </div>
  );
}

function ProjectRow({ project, onClick }) {
  const s = STATUS_COLORS[project.status] || STATUS_COLORS['New'];
  return (
    <div
      className="flex items-center justify-between py-3 px-4 rounded-card cursor-pointer transition-colors hover:bg-black/3"
      onClick={onClick}
      data-testid={`project-row-${project.id}`}
    >
      <div className="flex items-center gap-3">
        <CircleDot size={14} style={{ color: s.color }} />
        <div>
          <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{project.name}</p>
          <p className="text-xs" style={{ color: 'var(--muted)' }}>{project.client_name || 'Unknown'} · {project.project_type}</p>
        </div>
      </div>
      <span
        className="badge-status"
        style={{ background: s.bg, color: s.color }}
      >
        {project.status}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  useEffect(() => {
    settingsApi.dashboardStats()
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-black" style={{ color: 'var(--ink)' }}>
          {greeting}, {user?.name?.split(' ')[0] || 'Operator'}
        </h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--muted)' }}>
          {now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* Stats grid */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card-base p-5 h-24 animate-pulse" style={{ background: 'var(--border)' }} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <StatCard icon={Users} label="Total Clients" value={stats?.total_clients || 0} color="#006080" />
          <StatCard icon={TrendingUp} label="Active Projects" value={stats?.active_projects || 0} color="#006080" />
          <StatCard icon={Bug} label="Open Bugs" value={stats?.open_bugs || 0} color="#B34A3A" subtext="Across all projects" />
          <StatCard icon={FileText} label="Pending Estimates" value={stats?.pending_estimates || 0} color="#D9A441" />
          <StatCard icon={Clock} label="Pending Invoices" value={stats?.pending_invoices || 0} color="#E07A3C" />
          <StatCard icon={LayoutDashboard} label="Total Projects" value={stats?.total_projects || 0} color="#3C8A5A" />
        </div>
      )}

      {/* Recent projects */}
      <div className="grid lg:grid-cols-5 gap-4">
        <div className="lg:col-span-3 card-base overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
            <h2 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Recent Projects</h2>
            <button
              className="text-xs font-semibold flex items-center gap-1 transition-colors hover:opacity-70"
              style={{ color: '#006080' }}
              onClick={() => navigate('/admin/pipeline')}
              data-testid="view-all-projects"
            >
              View all <ArrowRight size={12} />
            </button>
          </div>
          <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
            {stats?.recent_projects?.length > 0 ? (
              stats.recent_projects.map(p => (
                <ProjectRow
                  key={p.id}
                  project={p}
                  onClick={() => navigate(`/admin/clients/${p.client_id}/projects/${p.id}`)}
                />
              ))
            ) : (
              <div className="py-8 text-center">
                <p className="text-sm" style={{ color: 'var(--muted)' }}>No projects yet.</p>
                <button
                  className="mt-2 text-xs font-semibold"
                  style={{ color: '#006080' }}
                  onClick={() => navigate('/admin/clients')}
                  data-testid="create-first-project"
                >
                  Add your first client →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Quick actions */}
        <div className="lg:col-span-2 space-y-3">
          <div className="card-base p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={15} style={{ color: '#006080' }} />
              <h2 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Quick Actions</h2>
            </div>
            <div className="space-y-2">
              {[
                { label: 'Add new client', to: '/admin/clients', color: '#006080' },
                { label: 'View pipeline', to: '/admin/pipeline', color: '#3C8A5A' },
                { label: 'Configure AI skills', to: '/admin/settings/ai', color: '#E07A3C' },
                { label: 'Manage brands', to: '/admin/settings/brands', color: '#5B2D8E' },
              ].map(a => (
                <button
                  key={a.label}
                  className="flex items-center justify-between w-full px-3 py-2.5 rounded-btn text-sm font-medium transition-colors hover:opacity-80"
                  style={{ background: `${a.color}10`, color: a.color }}
                  onClick={() => navigate(a.to)}
                  data-testid={`quick-action-${a.label.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {a.label}
                  <ArrowRight size={14} />
                </button>
              ))}
            </div>
          </div>

          <div className="card-base p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={15} style={{ color: '#D9A441' }} />
              <h2 className="font-bold text-sm" style={{ color: 'var(--ink)' }}>Attention Needed</h2>
            </div>
            <div className="space-y-2">
              {stats?.open_bugs > 0 && (
                <div className="text-xs p-2.5 rounded-btn" style={{ background: '#B34A3A15', color: '#B34A3A' }}>
                  {stats.open_bugs} open bug{stats.open_bugs > 1 ? 's' : ''} across projects
                </div>
              )}
              {stats?.pending_invoices > 0 && (
                <div className="text-xs p-2.5 rounded-btn" style={{ background: '#E07A3C15', color: '#E07A3C' }}>
                  {stats.pending_invoices} invoice{stats.pending_invoices > 1 ? 's' : ''} awaiting payment
                </div>
              )}
              {stats?.pending_estimates > 0 && (
                <div className="text-xs p-2.5 rounded-btn" style={{ background: '#D9A44115', color: '#D9A441' }}>
                  {stats.pending_estimates} estimate{stats.pending_estimates > 1 ? 's' : ''} pending approval
                </div>
              )}
              {!stats?.open_bugs && !stats?.pending_invoices && !stats?.pending_estimates && (
                <div className="text-xs p-2.5 rounded-btn flex items-center gap-2" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>
                  <CheckCircle2 size={14} />
                  All caught up!
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
